import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import moment from 'moment';

import type { PopupData, Service, ValidationError, ValidationErrors } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { hasOccurredWithin24Hours } from 'webapp/utils/common';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormErrorComponent from 'webapp/components/form-error';
import FormEmailAdminRecipientsComponent from 'webapp/components/form/email-admin-recipients';
import FormTextareaComponent from 'webapp/components/form/textarea';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

class RequestModel {
	@tracked recipients: string[];

	constructor(recipients: string[]) {
		this.recipients = recipients;
	}
}

type UserWhoSentRequest = Record<string, number>;

export default class BillingPopupRequestUpgradeComponent extends PopupInnerModalComponent<PopupData.Billing.RequestUpgrade> {
	@service('request') declare requestService: Service.Request;
	@service('billing') declare billingService: Service.Billing;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('timer') declare timerService: Service.Timer;

	@tracked errors: ValidationErrors<'recipients'> = {};
	@tracked modelWithEmailRecipients: RequestModel;
	@tracked text = '';
	@tracked usersWhoSentRequest: UserWhoSentRequest;

	localStorageKey = 'popupRequestUpgradeSentUsers';

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.modelWithEmailRecipients = new RequestModel(this.popupData.recipients);
		this.usersWhoSentRequest = JSON.parse(
			localStorage.getItem(this.localStorageKey) || '{}',
		) as UserWhoSentRequest;
	}

	get feature() {
		return this.popupData.feature;
	}

	get mainFeature() {
		return this.feature === BillingFeature.AI_DATABOARDS
			? BillingFeature.GENERATIVE_AI
			: this.feature;
	}

	get targetPlan() {
		return (
			this.billingService.getNextPlanForFeatureWithSameBillingCycle(this.mainFeature) || null
		);
	}

	get featureName() {
		return this.billingService.getFeatureName(this.feature) || this.feature;
	}

	get defaultText(): string {
		const targetPlanName = this.targetPlan ? `${this.targetPlan.name}` : 'higher';
		return `I’d like to use ${this.featureName} and need access to a ${targetPlanName} plan to do that. Could you help me with this and upgrade the subscription? Thank you!`;
	}

	get errorsArray(): ValidationError[] {
		const errorsArray: ValidationError[] = [],
			errors = this.errors;
		(Object.keys(errors) as (keyof typeof errors)[]).forEach((key) => {
			errorsArray.push({
				name: key,
				message: errors[key]![0]!.message,
			});
		});
		return errorsArray;
	}

	get isRequestButtonDisabled(): boolean {
		this.timerService.observe();

		const currentUserId = this.currentUserService.id;
		if (currentUserId && this.usersWhoSentRequest[currentUserId]) {
			return hasOccurredWithin24Hours(this.usersWhoSentRequest[currentUserId]);
		}

		return false;
	}

	get tooltipText() {
		if (this.isRequestButtonDisabled) {
			return 'Request has already been sent';
		}
		return undefined;
	}

	validateForm(): boolean {
		const errors: ValidationErrors<'recipients'> = {};

		if (!this.modelWithEmailRecipients.recipients.length) {
			errors.recipients = [
				{ name: 'recipients', message: 'At least one recipient must be selected' },
			];
		}

		this.errors = errors;

		return Object.keys(errors).length === 0;
	}

	@action
	requestUpgrade(): void {
		if (this.submitInProgress || !this.validateForm()) {
			return;
		}

		this.submitInProgress = true;

		const text = this.text,
			personalMessage = text ? text : this.defaultText,
			feature = this.mainFeature,
			data = {
				recipients: this.modelWithEmailRecipients.recipients,
				personalMessage,
				featureType: feature,
			};

		this.requestService
			.api('POST', '/account/feature-limitation', data)
			.then(() => {
				this.infolayerService.ok('Upgrade request sent!');
				this.analyticsService.event(TrackingEvent.UPGRADE_INVITE_SENT, {
					feature,
				});

				const currentUserId = this.currentUserService.id;

				let usersWhoSentRequest;
				if (currentUserId) {
					usersWhoSentRequest = {
						[currentUserId]: moment().unix(),
					};
					this.usersWhoSentRequest = usersWhoSentRequest;
					localStorage.setItem(this.localStorageKey, JSON.stringify(usersWhoSentRequest));
				}

				this.scheduleReposition();
			})
			.finally(() => {
				this.submitInProgress = false;
				this.close();
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	@action
	prefillDefault(): void {
		if (!this.text) {
			this.text = this.defaultText;
		}
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name="ArrowUpCircle" @size="lg" />
			Request plan upgrade
		</div>

		<div class="content">

			<p>Select admins to whom you'd like to send the update plan request.</p>
			<FormErrorComponent @errors={{this.errorsArray}} />
			<FormEmailAdminRecipientsComponent
				@model={{this.modelWithEmailRecipients}}
				@errors={{this.errors}}
				@submitInProgress={{this.submitInProgress}}
				@onContentChange={{this.scheduleReposition}}
				@isCustomAddDisabled={{true}}
			/>
			<div class="row">
				<FormTextareaComponent
					name="text"
					placeholder="Include a personal message (optional)"
					disabled={{this.submitInProgress}}
					@value={{this.text}}
					@focusIn={{this.prefillDefault}}
					@hideFloatLabel={{true}}
				/>
			</div>
			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text="Send upgrade request"
						@extraClass="save"
						@onTrigger={{this.requestUpgrade}}
						@submitInProgress={{this.submitInProgress}}
						@isDisabled={{this.isRequestButtonDisabled}}
						@tooltipText={{this.tooltipText}}
					/>
				</div>
			</div>

		</div>
	</template>
}
